// IE Hub - United Utilities Smart Suggestion / Document Proof Component
// Dynamic document upload prompt triggered by benefit/income entries

import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  HelpOutlineIcon,
  InsertDriveFileOutlinedIcon,
  VisibilityOutlinedIcon,
  DeleteOutlineIcon,
  AutoAwesomeIcon,
  Skeleton,
} from "../../../ui";
import { MAX_DOCS_WARNING } from "@/shared/lib/utils/documents";
import CollapsibleTextArea from "../../CollapsibleTextArea";
import { openConfirmDialog } from "@/components/ConfirmDialog";
import { useAlert } from "@/app/providers/AlertProvider";
import { UU_SHOW_HIDE_INFO } from "@/shared/uuShowHide";

export interface UploadedDocumentInfo {
  /** Document file name */
  name: string;
  /** File size (preserves backend units like KB/MB) */
  size: string | number;
  /** Upload date */
  uploadDate: Date;
  /** File object or URL */
  file?: File | string;
  /** Backend document record identifier */
  id?: number;
  /** Backend documentId (used for downloads) */
  documentId?: number;
  /** Share identifier used to associate documents with a UU field */
  shareId?: string;
  /** Friendly description stored in IE Hub */
  description?: string;
  /** Original filename as stored in the backend */
  fileName?: string;
}

export interface EvidenceItem {
  /** Label like "Primary evidence:" or "Secondary evidence:" */
  label: string;
  /** Description text */
  description: string;
  /** Highlighted requirement text (shown in golden color) */
  highlight?: string;
}

export interface SmartSuggestionProps {
  headerTitle: string;

  /** Title of the card (e.g., "Universal Credit proof") */
  title: string;
  /** Main evidence needed description */
  evidenceDescription?: React.ReactNode;
  /** Evidence items with labels, descriptions, and highlights */
  evidenceItems?: EvidenceItem[];
  /** Document must include text (shown with golden highlight) */
  documentMustInclude?: string;
  /** Question mark tooltip content / help text */
  helpText?: string;
  /** Callback when upload button is clicked - receives the selected file */
  onUpload?: (file: File, description?: string) => void;
  /** Callback when document is deleted */
  onDelete?: () => void;
  /** Callback when view/preview is clicked */
  onView?: (document: UploadedDocumentInfo) => void;
  /** Pre-uploaded document (controlled mode) */
  uploadedDocument?: UploadedDocumentInfo | null;
  /** Whether upload is in progress */
  isUploading?: boolean;
  /** Custom styling */
  sx?: Record<string, unknown>;
  /** Callback to open detailed help dialog/slider */
  onHelpClick?: () => void;
  /** Whether to show upload button */
  showUpload?: boolean;
  /** Callback when description changes (debounced auto-save) */
  onDescriptionChange?: (description: string) => void;
  /** Label for the notes/description text area */
  notesLabel?: string;
  /** Placeholder text for the notes/description text area */
  notesPlaceholder?: string;
  /** Max length for notes/description text */
  notesMaxLength?: number;
  /** Whether a document fetch is in-flight (prevents flashing upload button) */
  isDocumentLoading?: boolean;
}

const SmartSuggestionUU: React.FC<SmartSuggestionProps> = ({
  headerTitle,
  title,
  evidenceDescription,
  evidenceItems = [],
  documentMustInclude,
  helpText = "Click for more information",
  onUpload,
  onDelete,
  onView,
  uploadedDocument,
  isUploading = false,
  sx = {},
  onHelpClick,
  showUpload = true,
  onDescriptionChange,
  notesLabel = "Notes",
  notesPlaceholder = "Please provide any additional information about your Universal Credit",
  notesMaxLength = 200,
  isDocumentLoading = false,
}) => {
  const [description, setDescription] = useState<string>("");
  const lastSyncedDescriptionRef = useRef<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True while a document delete is in progress.
  // Notes auto-save (PUT) must stay off during delete, otherwise clearing the
  // notes field can fire an update call at the same time as delete (DELETE).
  const isDeletingDocumentRef = useRef(false);
  const { showAlert } = useAlert();

  useEffect(() => {
    // Keep local state in sync with the latest description from the server,
    // but avoid overwriting user edits (e.g., when clearing the field) with
    // stale values returned by the API for the same document.
    if (!uploadedDocument) {
      setDescription("");
      lastSyncedDescriptionRef.current = "";
      return;
    }

    const nextDescription = uploadedDocument.description ?? "";
    if (nextDescription !== lastSyncedDescriptionRef.current) {
      setDescription(nextDescription);
      lastSyncedDescriptionRef.current = nextDescription;
    }
  }, [uploadedDocument]);

  // Debounced auto-save for notes/description (800ms after the user stops typing)
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Do not save notes while delete is running — see handleDelete below
    if (isDeletingDocumentRef.current) {
      return;
    }

    // Only trigger save if we have a handler and there's a document uploaded
    if (onDescriptionChange && uploadedDocument) {
      debounceTimerRef.current = setTimeout(() => {
        // Re-check in case delete started during the 800ms wait
        if (isDeletingDocumentRef.current) {
          return;
        }
        onDescriptionChange(description);
      }, 800);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [description, onDescriptionChange, uploadedDocument]);

  const handleUploadClick = useCallback(async () => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && onUpload) {
        const allowedTypes = [
          "application/pdf",
          "image/png",
          "image/jpg",
          "image/jpeg",
        ];

        if (
          parseInt(localStorage.getItem("totalDocumentsLength") || "0") >= 10
        ) {
          showAlert({
            severity: "warning",
            message: MAX_DOCS_WARNING,
            autoHide: true,
            duration: 5000,
          });
          return;
        }

        if (!allowedTypes.includes(file.type)) {
          showAlert({
            severity: "error",
            message:
              "Invalid file format. Please upload PDF, PNG, JPG, or JPEG files.",
            autoHide: true,
            duration: 3000,
          });
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          showAlert({
            severity: "error",
            message:
              "The file is too large. Please select a file smaller than 5MB.",
            duration: 3000,
          });
          return;
        }
        Promise.resolve(onUpload(file, description)).catch(() => undefined);
      }
      if (event.target) {
        event.target.value = "";
      }
    },
    [onUpload, description]
  );

  const handleDelete = useCallback(async () => {
    const yes = await openConfirmDialog({
      title: "Delete Document",
      message: "Are you sure you want to delete this?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!yes) {
      return;
    }

    if (!onDelete) {
      return;
    }

    // Block notes auto-save for the whole delete — prevents PUT + DELETE race
    isDeletingDocumentRef.current = true;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    try {
      // Wait for delete (DELETE API) to finish before updating the UI.
      // Do not clear notes here; they clear when uploadedDocument is removed.
      await Promise.resolve(onDelete());
    } catch {
      // onDelete already shows an error — keep notes as they were
    } finally {
      isDeletingDocumentRef.current = false;
    }
  }, [onDelete]);

  const handleView = useCallback(() => {
    if (uploadedDocument && onView) {
      Promise.resolve(onView(uploadedDocument)).catch(() => undefined);
    }
  }, [uploadedDocument, onView]);

  const handleHelpIconClick = useCallback(() => {
    if (onHelpClick) {
      onHelpClick();
    }
  }, [onHelpClick]);

  const formatFileSize = (size?: string | number): string => {
    if (size === null || size === undefined) {
      return "";
    }

    // Preserve provided units when present
    if (typeof size === "string") {
      const trimmed = size.trim();
      if (!trimmed) return "";
      const hasUnits = /kb|mb|bytes?/i.test(trimmed);
      if (hasUnits) return trimmed;

      const numeric = Number(trimmed.replace(/[^0-9.]/g, ""));
      if (Number.isFinite(numeric)) {
        return `${numeric} KB`;
      }
      return trimmed;
    }

    if (!Number.isFinite(size)) return "";

    const bytes = size as number;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatUploadDate = (date: Date): string => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  // Golden/amber color for highlighted requirements
  const highlightColor = "#996B1F";

  // Rainbow gradient for left border
  const gradientBorder =
    "linear-gradient(180deg, #C289FC 0%, #378EFF 52%, #EAB308 100%)";
  const allowUpload = showUpload && Boolean(onUpload);
  const shouldRenderDocumentBlock =
    Boolean(uploadedDocument) || allowUpload || isDocumentLoading;
  const shouldShowSkeleton = isDocumentLoading && !uploadedDocument;

  return (
    <>
    { UU_SHOW_HIDE_INFO.uuShowHideInfo ?
    null : (
       <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-1"
      sx={{
        position: "relative",
        width: "100%",
        ...sx,
      }}
    >
      {/* Rainbow Gradient Left Border */}
      <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-2"
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          background: gradientBorder,
        }}
      />

      {/* Content Container with left padding for gradient */}
      <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-3"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          width: "100%",
          padding: "0px 16px 0px 12px",
          gap: "12px",
          py: 1.25,
        }}
      >
        {/* Header with Smart Suggestion title and help icon */}
        <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-4"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 0.75,
            width: "100%",
          }}
        >
          {/* Sparkle Icon */}
          <AutoAwesomeIcon
            sx={{
              fontSize: 18,
              color: "#6366F1",
            }}
          />
          <Typography data-testid="components-uu-uu-ui-smart-suggestion-uu-text-action-1"
            sx={{
              fontWeight: 600,
              color: "#1F2937",
              fontSize: "14px",
              lineHeight: 1.4,
            }}
          >
            {headerTitle ? headerTitle : "Smart Suggestion"}
          </Typography>

          {/* Help Icon */}
          <Tooltip title={helpText} placement="top" arrow>
            <IconButton data-testid="components-uu-uu-ui-smart-suggestion-uu-btn-handle-help-icon-click"
              size="small"
              onClick={handleHelpIconClick}
              sx={{
                width: 20,
                height: 20,
                color: "#9CA3AF",
                flexShrink: 0,
                p: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: onHelpClick ? "primary.main" : "text.secondary",
                },
              }}
              aria-label="Help information"
            >
              <HelpOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Title and Evidence Description combined in a single Box */}
        <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-5"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {/* Title as subheading if provided */}
          {title && title !== "Smart Suggestion" && false && (
            <Typography data-testid="components-uu-uu-ui-smart-suggestion-uu-text-fordeclaration:"
              sx={{
                fontWeight: 400,
                color: "#374151",
                fontSize: "14px",
                lineHeight: 1.5,
              }}
            >
              For{" "}
              <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-6" component="span" sx={{ color: "#3B82F6", fontWeight: 600 }}>
                {title}
              </Box>{" "}
              declaration:
            </Typography>
          )}

          {/* Evidence Description */}
          {evidenceDescription && (
            <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-7"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {String(evidenceDescription)
                .split("\n")
                .map((line, idx) => (
                  <Typography data-testid="components-uu-uu-ui-smart-suggestion-uu-text-action-2"
                    key={idx}
                    component="div"
                    sx={{
                      color: "#4B5563",
                      lineHeight: 1.5,
                      fontSize: "14px",
                    }}
                  >
                    {line
                      .split(
                        /(\bUniversal Credit journal Screenshot\b|\bBenefit Evidence\b)/gi
                      )
                      .map((part, i) =>
                        /Universal Credit journal Screenshot|Benefit Evidence/i.test(
                          part
                        ) ? (
                          <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-8"
                            key={i}
                            component="span"
                            sx={{ fontWeight: 600, color: "#1F2937" }}
                          >
                            {part}
                          </Box>
                        ) : (
                          part
                        )
                      )}
                  </Typography>
                ))}
            </Box>
          )}
        </Box>

        {/* Evidence Items */}
        {evidenceItems.length > 0 && (
          <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-9" sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {evidenceItems.map((item, index) => (
              <Typography data-testid="components-uu-uu-ui-smart-suggestion-uu-text-action-3"
                key={index}
                sx={{
                  fontSize: "14px",
                  lineHeight: 1.5,
                }}
              >
                <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-10"
                  component="span"
                  sx={{ fontWeight: 600, color: "#1F2937" }}
                >
                  {item.label}
                </Box>{" "}
                <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-11" component="span" sx={{ color: "#6B7280" }}>
                  {item.description}
                </Box>
                {item.highlight && (
                  <>
                    {" — "}
                    <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-12" component="span" sx={{ color: highlightColor }}>
                      {item.highlight}
                    </Box>
                  </>
                )}
              </Typography>
            ))}
          </Box>
        )}

        {/* Document Must Include - styled as Note */}
        {documentMustInclude && (
          <Typography data-testid="components-uu-uu-ui-smart-suggestion-uu-text-action-4"
            sx={{
              fontSize: "13px",
              lineHeight: 1.5,
              color: "#6B7280",
            }}
          >
            <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-note:" component="span" sx={{ fontWeight: 500, color: "#4B5563" }}>
              Note:
            </Box>{" "}
            {documentMustInclude}
          </Typography>
        )}

        {/* Document upload and review */}
        {shouldRenderDocumentBlock && (
          <>
            {shouldShowSkeleton && (
              <>
                {/* Skeleton while fetching document */}
                <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-13"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.25,
                    backgroundColor: "#F1F5F9",
                    borderRadius: "8px",
                  }}
                >
                  <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-14"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={32}
                      height={32}
                      sx={{ borderRadius: "6px" }}
                    />
                    <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-15" sx={{ flex: 1, minWidth: 0 }}>
                      <Skeleton variant="text" width="80%" height={18} />
                      <Skeleton variant="text" width="60%" height={14} />
                    </Box>
                  </Box>
                  <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-16" sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    <Skeleton variant="circular" width={28} height={28} />
                    <Skeleton variant="circular" width={28} height={28} />
                  </Box>
                </Box>
                <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-17" sx={{ mt: 1, width: "100%" }}>
                  <Skeleton
                    variant="rectangular"
                    height={72}
                    sx={{ borderRadius: "8px" }}
                  />
                </Box>
              </>
            )}

            {uploadedDocument && !shouldShowSkeleton && (
              <>
                {/* Uploaded Document Display */}
                <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-18"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.25,
                    backgroundColor: "#F1F5F9",
                    borderRadius: "8px",
                  }}
                >
                  <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-19"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {/* Document Icon */}
                    <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-20"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        backgroundColor: "#E2E8F0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <InsertDriveFileOutlinedIcon
                        sx={{ fontSize: 18, color: "#64748B" }}
                      />
                    </Box>

                    {/* Document Info */}
                    <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-21" sx={{ flex: 1, minWidth: 0 }}>
                      <Typography data-testid="components-uu-uu-ui-smart-suggestion-uu-text-action-5"
                        sx={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#1E293B",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {uploadedDocument.name}
                      </Typography>
                      <Typography data-testid="components-uu-uu-ui-smart-suggestion-uu-text-•-uploaded"
                        sx={{
                          fontSize: "11px",
                          color: "#64748B",
                        }}
                      >
                        {formatFileSize(uploadedDocument.size)} • Uploaded{" "}
                        {formatUploadDate(uploadedDocument.uploadDate)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action Icons */}
                  <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-22" sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                    <Tooltip title="View document" placement="top">
                      <IconButton data-testid="components-uu-uu-ui-smart-suggestion-uu-btn-handle-view"
                        size="small"
                        onClick={handleView}
                        sx={{
                          width: 28,
                          height: 28,
                          color: "#64748B",
                          "&:hover": {
                            color: "#3B82F6",
                            backgroundColor: "#E0E7FF",
                          },
                        }}
                      >
                        <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete document" placement="top">
                      <IconButton data-testid="components-uu-uu-ui-smart-suggestion-uu-btn-handle-delete"
                        size="small"
                        onClick={handleDelete}
                        sx={{
                          width: 28,
                          height: 28,
                          color: "#EF4444",
                          "&:hover": {
                            color: "#DC2626",
                            backgroundColor: "#FEE2E2",
                          },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Notes / Description (matches standard notes layout) */}
                <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-23" sx={{ mt: 1, width: "100%" }}>
                  <CollapsibleTextArea data-testid={`components-uu-uu-ui-smart-suggestion-uu-input-${notesLabel}`}
                    label={notesLabel}
                    value={description}
                    onChange={setDescription}
                    placeholder={notesPlaceholder}
                    maxLength={notesMaxLength}
                    autoExpandOnValue={false}
                    initiallyExpanded={false}
                  />
                </Box>
              </>
            )}

            {/* Only show upload button when no document is uploaded (single document per field) */}
            {allowUpload && !uploadedDocument && !isDocumentLoading && (
              <>
                {/* Hidden File Input */}
                <input data-testid="components-uu-uu-ui-smart-suggestion-uu-input-handle-file-change"
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />

                {/* Upload Button - Styled like the image */}
                <Box data-testid="components-uu-uu-ui-smart-suggestion-uu-container-action-24"
                  onClick={!isUploading ? handleUploadClick : undefined}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: isUploading ? "not-allowed" : "pointer",
                    pointerEvents: isUploading ? "none" : "auto",
                    opacity: isUploading ? 0.7 : 1,
                    py: 0.75,
                    px: 1.5,
                    backgroundColor: "#EFF6FF",
                    borderRadius: "6px",
                    border: "1px solid #DBEAFE",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#DBEAFE",
                    },
                  }}
                  role="button"
                  aria-disabled={isUploading}
                >
                  <Typography data-testid="components-uu-uu-ui-smart-suggestion-uu-text-action-6"
                    component="span"
                    sx={{
                      color: "#2563EB",
                      fontWeight: 500,
                      fontSize: "13px",
                    }}
                  >
                    {isUploading ? "Uploading..." : "+ Upload documents"}
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Box> )}
  </>
  );
};

export default SmartSuggestionUU;
